import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Lottie from 'lottie-react';
import appPreviewAnimation from '../../assets/animations/app_preview.json';
import { trackEvent } from '../../firebase';
import { isMobile } from 'react-device-detect';

const HomeContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: url('/images/main_bg.jpg') no-repeat center center;
  background-size: cover;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Logo = styled.img`
  width: 212px;
  height: 60px;
  margin-top: 48px;
  @media (max-width: 500px) {
    width: 140px;
    height: 40px;
    margin-top: 32px;
  }
`;

const Title = styled.h1`
  font-family: 'a_FuturaRound';
  font-style: normal;
  font-weight: 700;
  font-size: 48px;
  line-height: 61px;
  text-align: center;
  margin-top: 36px;
  background: transparent;
  -webkit-text-stroke: 12px white;
  text-stroke: 12px white;
  paint-order: stroke fill;
  white-space: normal;
  padding: 0 20px;

  @media (max-width: 500px) {
    font-size: 32px;
    line-height: 46px;
    -webkit-text-stroke: 8px white;
    text-stroke: 8px white;
    margin-top: 24px;
  }
`;


const StoreButtons = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 20px;

  @media (max-width: 500px) {
    gap: 16px;
    margin-top: 8px;
  }
`;

const StoreButton = styled.div`
  width: 220px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  transition: opacity 0.2s ease;

  @media (max-width: 500px) {
    width: 180px;
    height: 58px;
  }

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.8;
  }
`;

const AppPreview = styled.div`
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  width: 100%;
  height: 644px;
  position: relative;
  margin-top: 56px;
  margin-bottom: 40px;
  @media (max-width: 500px) {
    height: 520px;
    margin-top: 40px;
  }
`;

const LottieContainer = styled.div`
  width: 100%;
  max-width: 600px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleSpan = styled.span<{ color: string }>`
  color: ${props => props.color};
`;

const Hero = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  flex: 1;
`;

const WidgetShowcase = styled.div`
  width: 100%;
  overflow: hidden;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
`;

const WidgetContainer = styled.div<{ 
  direction: 'left' | 'right';
  $marginLeft?: string;
  $marginRight?: string;
}>`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  position: relative;
  margin-left: ${props => props.$marginLeft || '5%'};
  margin-right: ${props => props.$marginRight || '5%'};
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 200px,
    black calc(100% - 200px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 200px,
    black calc(100% - 200px),
    transparent 100%
  );
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 500px) {
    margin-left: 0% !important;
    margin-right: 0% !important;
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      black 50px,
      black calc(100% - 50px),
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0%,
      black 50px,
      black calc(100% - 50px),
      transparent 100%
    );
  }
`;

const scrollLeft = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const scrollRight = keyframes`
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
`;

const SCROLL_DURATION = '30s'; // 统一的动画时长

const WidgetContent = styled.div<{ direction: 'left' | 'right' }>`
  display: flex;
  gap: 20px;
  animation: ${props => props.direction === 'left' ? scrollLeft : scrollRight} ${SCROLL_DURATION} linear infinite;
  animation-play-state: running;
  will-change: transform;
  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
`;

const WidgetImage = styled.img`
  height: 120px;
  object-fit: contain;
  flex-shrink: 0;
`;

const Footer = styled.div`
  width: 100%;
  min-height: 80px;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding: 20px 0;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  padding: 0 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FooterLink = styled.a`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Home: React.FC = () => {
  useEffect(() => {
    trackEvent('app_start', {"user-agent":navigator.userAgent});
    const url = window.location.href;
    if (isMobile && ['/friend/code/', '/pet/code/', '/pet/gift/', '/pet/coparenting/', '/widget/'].filter((e) => url.includes(e)).length > 0) {
      const deeplink = url.replace('http://' + window.location.host, 'widgetable://jump.to').replace('https://' + window.location.host, 'widgetable://jump.to');
      console.log(deeplink);
      window.location.href = deeplink;
    }
  }, []);

  const handleStoreButtonClick = (store: 'ios' | 'android') => {
    const DOWNLOAD_LINK = {
      'android': "https://play.google.com/store/apps/details?id=com.widgetable.theme.android&referrer=utm_source%3Dwebsite_gp",
      'ios': "https://apps.apple.com/us/app/widgetable-lock-screen-widget/id1641107226?ppid=412e53d5-7f72-4228-a20a-374824e08e07",
    };
    const url = DOWNLOAD_LINK[store];
    url && window.location.assign(url);
    trackEvent('store_button_click', {
      store_type: store
    });
  };

  const handleFooterLinkClick = (link: string) => {
    trackEvent('footer_link_click', {
      link_type: link
    });
  };

  const widgetImages = [
    'widget_bubble_m.png',
    'widget_distance_s_1.png',
    'widget_fart_m.png',
    'widget_frequency_s.png',
    'widget_jar_m.png',
    'widget_sleep_week_s.png',
    'widget_pinit_m.png',
  ];

  const widgetImages2 = [
    'widget_distance_m_1.png',
    'widget_pet_s_1.png',
    'widget_missu_m.png',
    'widget_sleep_3weeks_m.png',
    'widget_plant_s.png',
    'widget_sleep_day_m.png',
  ];

  const widgetImages3 = [
    'widget_distance_m_2.png',
    'widget_poop_m.png',
    'widget_sleep_3weeks_s.png',
    'widget_status_m.png',
    'widget_missu_s.png',
    'widget_sleep_week_m.png',
    'widget_water_m.png'
  ];

  const renderWidgetRow = (
    direction: 'left' | 'right', 
    images: string[], 
    marginLeft?: string, 
    marginRight?: string
  ) => (
    <WidgetContainer 
      direction={direction}
      $marginLeft={marginLeft}
      $marginRight={marginRight}
    >
      <WidgetContent direction={direction}>
        {images.map((image, index) => (
          <WidgetImage
            key={index}
            src={`/images/widget/${image}`}
            alt={`Widget ${index + 1}`}
          />
        ))}
        {/* 复制一份图片以实现无缝滚动 */}
        {images.map((image, index) => (
          <WidgetImage
            key={`duplicate-${index}`}
            src={`/images/widget/${image}`}
            alt={`Widget1 ${index + 1}`}
          />
        ))}
        {images.map((image, index) => (
          <WidgetImage
            key={`duplicate2-${index}`}
            src={`/images/widget/${image}`}
            alt={`Widget2 ${index + 1}`}
          />
        ))}
      </WidgetContent>
    </WidgetContainer>
  );

  return (
    <HomeContainer>
      <Hero>
        <Logo src="/images/title_logo.png" alt="Widgetable Logo" />
        <Title>
          <TitleSpan color="#57B633">Widget Together, </TitleSpan>
          <TitleSpan color="#FF9633">Share Whatever!</TitleSpan>
        </Title>
        <StoreButtons>
          <StoreButton 
            onClick={() => handleStoreButtonClick('ios')}
          >
            <img src="/images/app_store.png" alt="App Store" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </StoreButton>
          <StoreButton 
            onClick={() => handleStoreButtonClick('android')}
          >
            <img src="/images/google_play.png" alt="Google Play" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </StoreButton>
        </StoreButtons>
        <AppPreview>
          <WidgetShowcase>
            {renderWidgetRow('left', widgetImages.concat(widgetImages2, widgetImages3), '8%', '8%')}  {/* 第一行向左滚动 */}
            {renderWidgetRow('right', widgetImages2.concat(widgetImages3, widgetImages), '4%', '4%')} {/* 第二行向右滚动 */}
            {renderWidgetRow('left', widgetImages3.concat(widgetImages, widgetImages2), '12%', '12%')}  {/* 第三行向左滚动 */}
          </WidgetShowcase>
          <LottieContainer>
            <Lottie
              animationData={appPreviewAnimation}
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </LottieContainer>
        </AppPreview>
      </Hero>
      <Footer>
        <FooterContent>
          <FooterLeft>
            <FooterLink 
              href="mailto:service@widgetable.net"
              onClick={() => handleFooterLinkClick('email')}
            >
              Email: service@widgetable.net
            </FooterLink>
            <span>Copyright © 2022 Happeny Technology Pte. Ltd.</span>
          </FooterLeft>
          <FooterRight>
            <FooterLink 
              href="/privacy.html"
              onClick={() => handleFooterLinkClick('privacy_policy')}
            >
              Privacy Policy
            </FooterLink>
            <FooterLink 
              href="/terms.html"
              onClick={() => handleFooterLinkClick('terms_of_service')}
            >
              Terms of Service
            </FooterLink>
          </FooterRight>
        </FooterContent>
      </Footer>
    </HomeContainer>
  );
};

export default Home; 